<template lang="pug">
.inputAmount(:class="{ 'inputAmount--left': currencySymbolPosition === 'left' }")
  .inputAmount__currency {{ currencySymbol }}

  input(
    :id="inputId"
    ref="input"
    v-model="formattedValue"
    type="text"
    :aria-invalid="ariaInvalid"
    :aria-describedby="ariaDescribedby"
    :aria-required="ariaRequired"
    :aria-label="ariaLabel || `${label || 'Amount'} in ${currency}`"
    @input="updateValue"
    @focus="onFocus"
    @blur="onBlur"
  )
</template>

<script setup lang="ts">
  import { useTemplateRef } from 'vue'

  //
  // Props and events
  //

  export interface InputAmountProps {
    /** Locale used by Intl number formatting. */
    locale: string
    /** ISO 4217 currency code. */
    currency: string
    /** Whether decimal digits should be hidden. */
    isInteger?: boolean
    /** Maximum number of decimal digits. */
    decimals?: number
    /** Unique ID for the input element (for label association) */
    inputId?: string
    /** Label text for accessibility (used in aria-label if no visible label) */
    label?: string
    /** ARIA invalid state */
    ariaInvalid?: boolean
    /** ARIA describedby for error/help text */
    ariaDescribedby?: string
    /** ARIA required state */
    ariaRequired?: boolean
    /** ARIA label override */
    ariaLabel?: string
  }

  const props = withDefaults(defineProps<InputAmountProps>(), {
    isInteger: false,
    decimals: 2,
    inputId: undefined,
    label: undefined,
    ariaInvalid: undefined,
    ariaDescribedby: undefined,
    ariaRequired: undefined,
    ariaLabel: undefined,
  })

  const modelValue = defineModel<number>({ required: true })

  //
  // Composables
  //

  const input = useTemplateRef<HTMLInputElement>('input')

  //
  // Data
  //

  const decimals = ref<number>(props.isInteger ? 0 : props.decimals)
  const rawValue = ref<number>(modelValue.value)
  const formattedValue = ref<string>('0')

  //
  // Computed
  //

  const decimalSeparator = computed(() => new Intl.NumberFormat(props.locale).format(1.1).charAt(1))
  const currencySymbolPosition = computed(() =>
    getCurrencySymbolPosition(props.locale, props.currency),
  )
  const currencySymbol = computed(() => getCurrencySymbol(props.locale, props.currency))

  /**
   * Get the position of the currency symbol for a given locale
   *
   * @param locale - The locale identifier (e.g., 'es-ES').
   * @param currencyCode - The currency code (e.g., 'EUR').
   */

  //
  // Methods
  //

  function getCurrencySymbolPosition(locale: string, currencyCode: string) {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
    }).format(1)

    const digitIndex = formatted.search(/\d/)
    return digitIndex > 0 ? 'left' : 'right'
  }

  /**
   * Retrieves the currency symbol for a given currency code.
   *
   * @param locale - The locale identifier (e.g., 'es-ES').
   * @param currencyCode - The currency code (e.g., 'EUR').
   */
  function getCurrencySymbol(locale: string, currencyCode: string) {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
    })

    const formatted = formatter.format(0)
    const symbol = formatted.replace(/[0-9.,\s]/g, '')
    return symbol
  }

  /**
   * Parse the value to a number
   *
   * @param {string} value - The value to parse
   * @param {string} decimalSeparator - The decimal separator
   */
  function parseNumber(value: string, decimalSeparator: string) {
    if (value === '') return 0

    // Remove all characters that are not a number or a decimal separator,
    // replace the decimal separator by a dot because the dot is the only valid decimal separator in javascript
    const normalizedValue = value
      .replace(new RegExp(`[^0-9${decimalSeparator}]`, 'g'), '')
      .replace(decimalSeparator, '.')

    return parseFloat(normalizedValue)
  }

  /**
   * Format the number to a locale string
   * - Preserve trailing zeros in decimals.
   * - Fixes missing thousands separator for four-digit numbers in Spanish.
   *
   * @param {number} value - The numeric string to be formatted
   * @param {string} locale - The locale identifier (e.g., 'es-ES').
   * @param {number} decimals - The number of decimal places to display
   * @param {string} decimalSeparator - The decimal separator
   * @param {string} thousandSeparator - The thousand separator
   */
  function parseLocaleString(
    value: string,
    locale: string,
    decimals: number,
    decimalSeparator: string,
  ) {
    // To preserve trailing zeros in decimals, minimumFractionDigits is set to match the decimal count of the original value.
    // This ensures zeros are maintained in the formatted number for the specified locale.
    const numberOfDecimals = value.split(decimalSeparator)[1]?.length || 0
    const minimumFractionDigits = Math.min(numberOfDecimals, decimals)
    const maximumFractionDigits = decimals

    // parseFloat not accepting thousand separators and requires a dot for decimal separation.
    const normalizedValue = value
      .replace(new RegExp(`[^0-9${decimalSeparator}]`, 'g'), '')
      .replace(decimalSeparator, '.')

    const number = parseFloat(normalizedValue)

    if (isNaN(number)) return ''

    let formattedValue = new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(number)

    // Spanish locale has a specific behavior for the thousands separator
    // If the value is between 1000 and 9999, the thousands separator isn't displayed
    if (
      (locale === 'es-ES' || locale === 'es') &&
      Math.abs(number) >= 1000 &&
      Math.abs(number) < 10000
    ) {
      if (!formattedValue.includes('.')) {
        formattedValue = formattedValue.slice(0, 1) + '.' + formattedValue.slice(1)
      }
    }

    return formattedValue
  }

  /**
   * Update the value
   * - Prevents the cursor from jumping to the end of the input when the input's value is updated with its formatted value.
   *
   * @param {string} value - The value to update
   */
  function updateValue(event: Event) {
    const value = (event.target as HTMLInputElement).value

    // if last character is a decimal separator, do not update the value
    if (!props.isInteger && value.endsWith(decimalSeparator.value)) return

    const cursorPosition = input.value?.selectionStart ?? 0

    rawValue.value = parseNumber(value, decimalSeparator.value)
    formattedValue.value = parseLocaleString(
      value,
      props.locale,
      decimals.value,
      decimalSeparator.value,
    )

    modelValue.value = rawValue.value

    // Set the cursor position after the value has been updated
    setTimeout(() => {
      const lengthDifference = formattedValue.value.length - value.length
      input.value?.setSelectionRange(
        cursorPosition + lengthDifference,
        cursorPosition + lengthDifference,
      )
    }, 0)
  }

  /**
   * Set the raw and formatted value to 0 if the input is focused and the value is 0
   * Prevents the user from typing to the right of the zero
   *
   * @param {string} value - The value to check
   */
  function onFocus(event: Event) {
    const value = (event.target as HTMLInputElement).value

    input.value?.setSelectionRange(4, 4)
    if (value === '0') {
      rawValue.value = 0
      formattedValue.value = ''
    }
  }

  /**
   * Set the raw and formatted value to 0 if the input is blurred and the value is empty
   *
   * @param {string} value - The value to check
   */
  function onBlur(event: Event) {
    const value = (event.target as HTMLInputElement).value

    if (value === '') {
      rawValue.value = 0
      formattedValue.value = '0'
    }
  }

  /**
   * Watch the model value and update the raw and formatted value
   * if the model value is different from the raw value
   */

  //
  // Watchers
  //

  watch(
    () => modelValue.value,
    newValue => {
      if (newValue === rawValue.value) return

      rawValue.value = newValue
      formattedValue.value = parseLocaleString(
        newValue.toString(),
        props.locale,
        decimals.value,
        decimalSeparator.value,
      )
    },
  )

  //
  // Lifecycle Hooks
  //

  onMounted(() => {
    formattedValue.value = parseLocaleString(
      modelValue.value.toString(),
      props.locale,
      decimals.value,
      decimalSeparator.value,
    )
  })
</script>

<style lang="scss" scoped>
  .inputAmount {
    position: relative;
    display: inline-block;
  }

  .inputAmount--left {
    input {
      padding-left: triple();
      padding-right: simple();
      text-align: left;
    }

    .inputAmount__currency {
      left: 0;
      right: auto;
      border-radius: $border-radius 0 0 $border-radius;
    }
  }

  .inputAmount__currency {
    position: absolute;
    top: 0;
    right: 0;
    color: color(gray4);
    font-size: font-size(small1);
    width: double();
    height: 100%;
    border: 1px solid $border-color;
    border-radius: 0 $border-radius $border-radius 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $color-background;
  }

  input {
    border-radius: $border-radius;
    text-align: right;
    padding-right: triple();
    width: 100%;
  }
</style>
